'use client';

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  UserCheck,
  Database,
  Cog,
  Lock,
  Share2,
  Cookie,
  UserX,
  Mail,
  ChevronRight,
  Info
} from 'lucide-react';

const sections = [
  { id: 'introduction', title: '1. Introduction & Overview', icon: UserCheck },
  { id: 'collection', title: '2. Information We Collect', icon: Database },
  { id: 'usage', title: '3. How We Use Data', icon: Cog },
  { id: 'security', title: '4. Data Storage & Security', icon: Lock },
  { id: 'sharing', title: '5. Third-Party Sharing', icon: Share2 },
  { id: 'cookies', title: '6. Cookies Policy', icon: Cookie },
  { id: 'rights', title: '7. Your Rights & Choices', icon: UserX },
  { id: 'contact', title: '8. Contact Us', icon: Mail },
];

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

export default function PrivacyPageClient() {
  const [activeSection, setActiveSection] = useState('introduction');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-20% 0px -60% 0px', threshold: 0.1 }
    );

    sections.forEach((section) => {
      const el = document.getElementById(section.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const headerOffset = 100;
      const elementPosition = el.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setActiveSection(id);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white text-gray-800 font-dm-sans">
      {/* Hero Header Section */}
      <section className="relative py-24 overflow-hidden h-[40vh] min-h-[300px] flex items-center justify-center">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-black/60 z-10"></div>
          <img
            src="/images/palm-jumeirah.jpg"
            alt="Palm Jumeirah Dubai"
            className="w-full h-full object-cover object-center"
          />
        </div>

        {/* Content */}
        <div className="relative z-20 max-w-7xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl font-space-grotesk font-bold text-white mb-4">
              Privacy Policy
            </h1>
            <p className="text-lg text-gray-200 max-w-2xl mx-auto font-light">
              We value your privacy. Learn how 18Bricks collects, secures, and handles your personal information.
            </p>
            <div className="mt-4 text-sm text-[#FFC400] font-medium">
              Last Updated: May 2026
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content Layout */}
      <section className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">

          {/* Sidebar Table of Contents (Sticky) */}
          <div className="hidden lg:block lg:col-span-1">
            <div className="sticky top-28 bg-white rounded-xl shadow-md border border-gray-100 p-6 space-y-2">
              <h3 className="font-space-grotesk font-bold text-lg mb-4 text-gray-900 border-b pb-2">
                Table of Contents
              </h3>
              <nav className="flex flex-col space-y-1">
                {sections.map((section) => {
                  const IconComponent = section.icon;
                  const isActive = activeSection === section.id;
                  return (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-left text-sm font-medium transition-all duration-200 ${isActive
                        ? 'bg-[#FFC400]/10 text-gray-900 border-l-4 border-[#FFC400] pl-2 font-semibold'
                        : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50 border-l-4 border-transparent'
                        }`}
                    >
                      <IconComponent className={`w-4 h-4 shrink-0 ${isActive ? 'text-[#FFC400]' : 'text-gray-400'}`} />
                      <span className="truncate">{section.title}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Detailed Document Content */}
          <div className="col-span-1 lg:col-span-3 space-y-12">

            {/* Disclaimer Alert */}
            <motion.div
              variants={fadeInUp}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              className="bg-blue-50 border border-blue-200 rounded-xl p-5 flex gap-4 text-blue-900"
            >
              <Info className="w-6 h-6 text-blue-500 shrink-0 mt-0.5" />
              <div>
                <h4 className="font-bold font-space-grotesk text-base mb-1">Privacy Commitment</h4>
                <p className="text-sm text-blue-800 leading-relaxed">
                  18Bricks is committed to safeguarding your privacy online. This policy applies to all personal data collected through our booking portal, mobile services, and direct communication channels. We ensure alignment with the UAE Data Protection Law and general international data protection standards.
                </p>
              </div>
            </motion.div>

            {/* Sections */}
            <div className="space-y-12">

              {/* Section 1: Introduction */}
              <motion.div
                id="introduction"
                variants={fadeInUp}
                initial="initial"
                whileInView="animate"
                viewport={{ once: true }}
                className="scroll-mt-28 bg-white rounded-xl shadow-sm border border-gray-100 p-8 space-y-4"
              >
                <div className="flex items-center gap-3 border-b border-gray-100 pb-4 mb-4">
                  <div className="w-10 h-10 bg-[#FFC400]/10 rounded-lg flex items-center justify-center text-[#FFC400]">
                    <UserCheck className="w-5 h-5" />
                  </div>
                  <h2 className="text-2xl font-space-grotesk font-bold text-gray-900">1. Introduction & Overview</h2>
                </div>
                <p className="leading-relaxed">
                  At 18Bricks, we respect your privacy and are committed to protecting the personal data you share with us. This Privacy Policy details how we collect, process, store, and share your personal data when you visit our website, complete a reservation for our holiday homes in Dubai, or contact our support team.
                </p>
                <p className="leading-relaxed">
                  By utilizing our website and booking services, you consent to the data collection and processing practices described in this document.
                </p>
              </motion.div>

              {/* Section 2: Collection */}
              <motion.div
                id="collection"
                variants={fadeInUp}
                initial="initial"
                whileInView="animate"
                viewport={{ once: true }}
                className="scroll-mt-28 bg-white rounded-xl shadow-sm border border-gray-100 p-8 space-y-4"
              >
                <div className="flex items-center gap-3 border-b border-gray-100 pb-4 mb-4">
                  <div className="w-10 h-10 bg-[#FFC400]/10 rounded-lg flex items-center justify-center text-[#FFC400]">
                    <Database className="w-5 h-5" />
                  </div>
                  <h2 className="text-2xl font-space-grotesk font-bold text-gray-900">2. Information We Collect</h2>
                </div>
                <p className="leading-relaxed">
                  We collect information that identifies or relates to you to deliver our holiday home rental services:
                </p>
                <ul className="list-disc pl-5 space-y-2 text-sm leading-relaxed text-gray-600">
                  <li><strong>Personal Identity Details:</strong> Full name, passport copies, nationality, date of birth, and Emirates ID (mandatory for Dubai Department of Economy and Tourism registrations).</li>
                  <li><strong>Contact Information:</strong> Phone number, email address, and home address.</li>
                  <li><strong>Booking & Stay History:</strong> Details of your holiday home rental bookings, check-in and check-out dates, preferences, and special service requests.</li>
                  <li><strong>Financial Data:</strong> Encrypted credit card details, billing address, and transaction logs processed securely by our certified payment providers.</li>
                  <li><strong>Technical & Usage Data:</strong> IP address, device type, browser settings, operating system, and navigation logs collected via cookies and trackers.</li>
                </ul>
              </motion.div>

              {/* Section 3: Usage */}
              <motion.div
                id="collection"
                variants={fadeInUp}
                initial="initial"
                whileInView="animate"
                viewport={{ once: true }}
                className="scroll-mt-28"
              /> {/* Spacer anchor */}

              <motion.div
                id="usage"
                variants={fadeInUp}
                initial="initial"
                whileInView="animate"
                viewport={{ once: true }}
                className="scroll-mt-28 bg-white rounded-xl shadow-sm border border-gray-100 p-8 space-y-4"
              >
                <div className="flex items-center gap-3 border-b border-gray-100 pb-4 mb-4">
                  <div className="w-10 h-10 bg-[#FFC400]/10 rounded-lg flex items-center justify-center text-[#FFC400]">
                    <Cog className="w-5 h-5" />
                  </div>
                  <h2 className="text-2xl font-space-grotesk font-bold text-gray-900">3. How We Use Your Information</h2>
                </div>
                <p className="leading-relaxed font-semibold text-gray-700">
                  We use your personal data to provide a seamless and secure luxury stay experience:
                </p>
                <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-600">
                  <div className="p-4 border border-gray-100 bg-gray-50 rounded-lg space-y-2">
                    <span className="font-bold text-gray-900 block">Operational Purposes</span>
                    <ul className="list-disc pl-4 space-y-1">
                      <li>Processing bookings and payments</li>
                      <li>Completing mandatory DTCM guest registrations</li>
                      <li>Communicating check-in/check-out guidelines</li>
                      <li>Providing 24/7 guest concierge support</li>
                    </ul>
                  </div>
                  <div className="p-4 border border-gray-100 bg-gray-50 rounded-lg space-y-2">
                    <span className="font-bold text-gray-900 block">Improvement & Legal Compliance</span>
                    <ul className="list-disc pl-4 space-y-1">
                      <li>Improving our website performance and features</li>
                      <li>Sending promotional offers and newsletters (with opt-out)</li>
                      <li>Fulfilling legal audits and tax requirements</li>
                      <li>Detecting and preventing booking fraud</li>
                    </ul>
                  </div>
                </div>
              </motion.div>

              {/* Section 4: Security */}
              <motion.div
                id="security"
                variants={fadeInUp}
                initial="initial"
                whileInView="animate"
                viewport={{ once: true }}
                className="scroll-mt-28 bg-white rounded-xl shadow-sm border border-gray-100 p-8 space-y-4"
              >
                <div className="flex items-center gap-3 border-b border-gray-100 pb-4 mb-4">
                  <div className="w-10 h-10 bg-[#FFC400]/10 rounded-lg flex items-center justify-center text-[#FFC400]">
                    <Lock className="w-5 h-5" />
                  </div>
                  <h2 className="text-2xl font-space-grotesk font-bold text-gray-900">4. Data Storage & Security Measures</h2>
                </div>
                <p className="leading-relaxed">
                  We implement robust technical and organizational security measures to protect your personal data against unauthorized access, loss, misuse, modification, or disclosure.
                </p>
                <p className="leading-relaxed">
                  Your information is stored on secure cloud servers with restricted access. Payment details are processed using Secure Socket Layer (SSL) encryption and are certified under the PCI-DSS standards. We never retain full credit card numbers on our local databases.
                </p>
              </motion.div>

              {/* Section 5: Sharing */}
              <motion.div
                id="sharing"
                variants={fadeInUp}
                initial="initial"
                whileInView="animate"
                viewport={{ once: true }}
                className="scroll-mt-28 bg-white rounded-xl shadow-sm border border-gray-100 p-8 space-y-4"
              >
                <div className="flex items-center gap-3 border-b border-gray-100 pb-4 mb-4">
                  <div className="w-10 h-10 bg-[#FFC400]/10 rounded-lg flex items-center justify-center text-[#FFC400]">
                    <Share2 className="w-5 h-5" />
                  </div>
                  <h2 className="text-2xl font-space-grotesk font-bold text-gray-900">5. Third-Party Sharing</h2>
                </div>
                <p className="leading-relaxed">
                  We do not sell, rent, or trade your personal data. We share your information only with trusted third parties under strict confidentiality protocols:
                </p>
                <div className="space-y-3 text-sm text-gray-600">
                  <div className="flex items-start gap-3">
                    <ChevronRight className="w-4 h-4 text-[#FFC400] shrink-0 mt-1" />
                    <p><strong>Government Authorities:</strong> We submit passport and stay details to the Dubai Department of Economy and Tourism (DET / DTCM) for legal guest registry compliance.</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <ChevronRight className="w-4 h-4 text-[#FFC400] shrink-0 mt-1" />
                    <p><strong>Payment Gateways:</strong> Secure third-party payment processors who verify and complete financial transactions.</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <ChevronRight className="w-4 h-4 text-[#FFC400] shrink-0 mt-1" />
                    <p><strong>Service Providers:</strong> Maintenance teams, housekeeping agencies, or specialized concierge partners who need specific details to fulfill requests (e.g. key handovers, maintenance visits).</p>
                  </div>
                </div>
              </motion.div>

              {/* Section 6: Cookies */}
              <motion.div
                id="cookies"
                variants={fadeInUp}
                initial="initial"
                whileInView="animate"
                viewport={{ once: true }}
                className="scroll-mt-28 bg-white rounded-xl shadow-sm border border-gray-100 p-8 space-y-4"
              >
                <div className="flex items-center gap-3 border-b border-gray-100 pb-4 mb-4">
                  <div className="w-10 h-10 bg-[#FFC400]/10 rounded-lg flex items-center justify-center text-[#FFC400]">
                    <Cookie className="w-5 h-5" />
                  </div>
                  <h2 className="text-2xl font-space-grotesk font-bold text-gray-900">6. Cookies & Tracking Technologies</h2>
                </div>
                <p className="leading-relaxed">
                  We use cookies and similar tracking technologies to enhance your browsing experience, analyze website traffic, and deliver personalized booking suggestions.
                </p>
                <p className="leading-relaxed">
                  You can set your browser settings to refuse some or all cookies, or alert you when cookies are being sent. Note that disabling cookies may affect the usability of some features on our booking portal.
                </p>
              </motion.div>

              {/* Section 7: Rights */}
              <motion.div
                id="rights"
                variants={fadeInUp}
                initial="initial"
                whileInView="animate"
                viewport={{ once: true }}
                className="scroll-mt-28 bg-white rounded-xl shadow-sm border border-gray-100 p-8 space-y-4"
              >
                <div className="flex items-center gap-3 border-b border-gray-100 pb-4 mb-4">
                  <div className="w-10 h-10 bg-[#FFC400]/10 rounded-lg flex items-center justify-center text-[#FFC400]">
                    <UserX className="w-5 h-5" />
                  </div>
                  <h2 className="text-2xl font-space-grotesk font-bold text-gray-900">7. Your Rights & Choices</h2>
                </div>
                <p className="leading-relaxed">
                  Depending on your jurisdiction and location, you have certain rights regarding your personal data:
                </p>
                <ul className="list-disc pl-5 space-y-2 text-sm leading-relaxed text-gray-600">
                  <li><strong>Access:</strong> The right to request copies of your personal data held by us.</li>
                  <li><strong>Correction:</strong> The right to request correction of inaccurate or incomplete details.</li>
                  <li><strong>Erasure:</strong> The right to request deletion of your data (subject to UAE legal holding periods for booking registries).</li>
                  <li><strong>Opt-Out:</strong> The right to unsubscribe from marketing emails at any time using the opt-out links.</li>
                </ul>
              </motion.div>

              {/* Section 8: Contact */}
              <motion.div
                id="contact"
                variants={fadeInUp}
                initial="initial"
                whileInView="animate"
                viewport={{ once: true }}
                className="scroll-mt-28 bg-white rounded-xl shadow-sm border border-gray-100 p-8 space-y-4"
              >
                <div className="flex items-center gap-3 border-b border-gray-100 pb-4 mb-4">
                  <div className="w-10 h-10 bg-[#FFC400]/10 rounded-lg flex items-center justify-center text-[#FFC400]">
                    <Mail className="w-5 h-5" />
                  </div>
                  <h2 className="text-2xl font-space-grotesk font-bold text-gray-900">8. Contact Us & Policy Updates</h2>
                </div>
                <p className="leading-relaxed">
                  We may update this Privacy Policy from time to time to reflect changes in legal requirements or operational practices. The updated policy will be posted on this page with the modified date.
                </p>
                <p className="leading-relaxed">
                  If you have any questions, concerns, or requests regarding this Privacy Policy or your personal information, please contact our Data Protection officer:
                </p>
                <div className="bg-gray-50 border border-gray-100 rounded-lg p-5 text-sm space-y-1">
                  <p><strong>Email:</strong> info@18bricksholidayhomes.ae</p>
                  <p><strong>Office Address:</strong> Quarter A, 102, Expo City Dubai, 8 Mangrove Avenue, Dubai, UAE</p>
                  <p><strong>Phone:</strong> +971 52 42 27546</p>
                </div>
              </motion.div>

            </div>
          </div>

        </div>
      </section>
    </div>
  );
}
